import React, { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt, fmtDate } from '../utils';
import {
  Card, CardHeader, StatCard, StatGrid, Tag, Badge, SearchInput,
  Skeleton, PageWrap, PageHeader, Table,
} from '../components/UI';
import '../styles/Registers.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Sales Register
═══════════════════════════════════════════════════════════════════════════ */
export function SalesRegister({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.salesRegister(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let r = data || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.raw_party?.toLowerCase().includes(q) ||
        x.voucher_number?.toLowerCase().includes(q) ||
        x.narration?.toLowerCase().includes(q) ||
        x.party_gstin?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, search]);

  const totalAmount  = (data || []).reduce((s, r) => s + parseFloat(r.invoice_amount || 0), 0);
  const totalTaxable = (data || []).reduce((s, r) => s + parseFloat(r.taxable_amount || 0), 0);
  const totalGST     = (data || []).reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const interState   = (data || []).filter(r => r.supply_type === 'Inter-state').length;
  const intraState   = (data || []).filter(r => r.supply_type === 'Intra-state').length;
  const noGST        = (data || []).filter(r => r.supply_type === 'Exempt/Non-GST').length;

  const getSupplyBadge = (type) => {
    if (type === 'Inter-state')    return { label: 'IGST',      variant: 'blue'  };
    if (type === 'Intra-state')    return { label: 'CGST+SGST', variant: 'green' };
    return                                { label: 'Exempt',    variant: 'gray'  };
  };

  const getAmountFlag = (amount) => {
    const a = parseFloat(amount) || 0;
    if (a >= 10000000) return { label: '≥1Cr', variant: 'red'   };
    if (a >= 1000000)  return { label: '≥10L', variant: 'amber' };
    return null;
  };

  return (
    <PageWrap>
      <PageHeader
        title="Sales Register"
        sub="All sales invoices for the period with GST breakdown"
      />

      <StatGrid cols={5}>
        <StatCard label="Invoices"       value={(data || []).length}    />
        <StatCard label="Total Sales"    value={`₹${fmt(totalAmount)}`} accent="blue" />
        <StatCard label="Taxable Value"  value={`₹${fmt(totalTaxable)}`} />
        <StatCard label="Total GST"      value={`₹${fmt(totalGST)}`}   accent="amber" />
        <StatCard label="Inter-state"    value={interState}             accent={interState > 0 ? 'blue' : undefined} />
      </StatGrid>

      {/* ── Supply type breakdown ── */}
      <div className="reg-supply-row">
        {[
          { label: 'Intra-state (CGST+SGST)', count: intraState, variant: 'green' },
          { label: 'Inter-state (IGST)',       count: interState, variant: 'blue'  },
          { label: 'Exempt / Non-GST',         count: noGST,      variant: 'gray'  },
        ].map(s => (
          <div key={s.label} className="reg-supply-chip">
            <Badge text={String(s.count)} variant={s.variant} />
            <span className="reg-supply-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <Card>
        <CardHeader
          title={`${rows.length} Sales Invoice${rows.length !== 1 ? 's' : ''}`}
          right={
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search party, voucher, GSTIN..."
            />
          }
        />
        {loading ? <Skeleton rows={6} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="reg-idx">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => <span className="reg-date">{fmtDate(r.date)}</span> },
              { label: 'Voucher', key: 'voucher_number',
                render: r => <Tag>{r.voucher_number}</Tag> },
              { label: 'Party', key: 'raw_party',
                render: r => (
                  <div>
                    <div className="reg-party-name">{r.raw_party || '—'}</div>
                    {r.party_gstin && <div className="reg-gstin">{r.party_gstin}</div>}
                  </div>
                )},
              { label: 'Supply Type', key: 'supply_type',
                render: r => {
                  const { label, variant } = getSupplyBadge(r.supply_type);
                  return <Badge text={label} variant={variant} />;
                }},
              { label: 'Narration', key: 'narration', maxWidth: 180,
                render: r => <span className="reg-narration">{r.narration || '—'}</span> },
              { label: 'Taxable (₹)', key: 'taxable_amount', right: true, mono: true,
                render: r => <span className="reg-mono">₹{fmt(r.taxable_amount)}</span> },
              { label: 'CGST (₹)', key: 'gst_cgst', right: true, mono: true,
                render: r => parseFloat(r.gst_cgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.gst_cgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'SGST (₹)', key: 'gst_sgst', right: true, mono: true,
                render: r => parseFloat(r.gst_sgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.gst_sgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'IGST (₹)', key: 'gst_igst', right: true, mono: true,
                render: r => parseFloat(r.gst_igst) > 0
                  ? <span className="reg-mono-accent">₹{fmt(r.gst_igst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'Total (₹)', key: 'invoice_amount', right: true, mono: true,
                render: r => (
                  <div className="reg-total-cell">
                    <span className="reg-mono-green">₹{fmt(r.invoice_amount)}</span>
                    {getAmountFlag(r.invoice_amount) && (
                      <div className="reg-flag-badge">
                        <Badge
                          text={getAmountFlag(r.invoice_amount).label}
                          variant={getAmountFlag(r.invoice_amount).variant}
                        />
                      </div>
                    )}
                  </div>
                )},
            ]}
            rows={rows.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No sales invoices found"
          />
        )}
      </Card>
    </PageWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Purchase Register
═══════════════════════════════════════════════════════════════════════════ */
export function PurchaseRegister({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.purchaseRegister(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let r = data || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.raw_party?.toLowerCase().includes(q) ||
        x.voucher_number?.toLowerCase().includes(q) ||
        x.narration?.toLowerCase().includes(q) ||
        x.party_gstin?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, search]);

  const totalAmount  = (data || []).reduce((s, r) => s + parseFloat(r.invoice_amount || 0), 0);
  const totalTaxable = (data || []).reduce((s, r) => s + parseFloat(r.taxable_amount || 0), 0);
  const totalITC     = (data || []).reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const interState   = (data || []).filter(r => r.supply_type === 'Inter-state').length;
  const noGSTIN      = (data || []).filter(r => !r.party_gstin).length;

  const getSupplyBadge = (type) => {
    if (type === 'Inter-state')    return { label: 'IGST',      variant: 'blue'  };
    if (type === 'Intra-state')    return { label: 'CGST+SGST', variant: 'green' };
    return                                { label: 'Exempt',    variant: 'gray'  };
  };

  const getAmountFlag = (amount) => {
    const a = parseFloat(amount) || 0;
    if (a >= 10000000) return { label: '≥1Cr', variant: 'red'   };
    if (a >= 1000000)  return { label: '≥10L', variant: 'amber' };
    return null;
  };

  return (
    <PageWrap>
      <PageHeader
        title="Purchase Register"
        sub="All purchase invoices for the period with ITC breakdown"
      />

      <StatGrid cols={5}>
        <StatCard label="Invoices"        value={(data || []).length}    />
        <StatCard label="Total Purchases" value={`₹${fmt(totalAmount)}`} accent="red" />
        <StatCard label="Taxable Value"   value={`₹${fmt(totalTaxable)}`} />
        <StatCard label="Total ITC"       value={`₹${fmt(totalITC)}`}   accent="green" />
        <StatCard
          label="No GSTIN"
          value={noGSTIN}
          accent={noGSTIN > 0 ? 'amber' : 'green'}
        />
      </StatGrid>

      {/* ── ITC alert ── */}
      {noGSTIN > 0 && (
        <div className="reg-itc-alert">
          <span className="reg-itc-icon">⚠</span>
          <div className="reg-itc-text">
            <strong>{noGSTIN} invoice{noGSTIN > 1 ? 's' : ''} from suppliers with no GSTIN.</strong> Input
            tax credit may not be available for purchases from unregistered suppliers.
            Verify whether RCM (Reverse Charge Mechanism) applies.
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <Card>
        <CardHeader
          title={`${rows.length} Purchase Invoice${rows.length !== 1 ? 's' : ''}`}
          right={
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search party, voucher, GSTIN..."
            />
          }
        />
        {loading ? <Skeleton rows={6} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="reg-idx">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => <span className="reg-date">{fmtDate(r.date)}</span> },
              { label: 'Voucher', key: 'voucher_number',
                render: r => <Tag>{r.voucher_number}</Tag> },
              { label: 'Party', key: 'raw_party',
                render: r => (
                  <div>
                    <div className="reg-party-name">{r.raw_party || '—'}</div>
                    {r.party_gstin ? (
                      <div className="reg-gstin">{r.party_gstin}</div>
                    ) : (
                      <div style={{ marginTop: 2 }}>
                        <Badge text="No GSTIN" variant="amber" />
                      </div>
                    )}
                  </div>
                )},
              { label: 'Group', key: 'party_group',
                render: r => r.party_group
                  ? <span className="reg-party-group">{r.party_group}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'Supply Type', key: 'supply_type',
                render: r => {
                  const { label, variant } = getSupplyBadge(r.supply_type);
                  return <Badge text={label} variant={variant} />;
                }},
              { label: 'Narration', key: 'narration', maxWidth: 160,
                render: r => <span className="reg-narration">{r.narration || '—'}</span> },
              { label: 'Taxable (₹)', key: 'taxable_amount', right: true, mono: true,
                render: r => <span className="reg-mono">₹{fmt(r.taxable_amount)}</span> },
              { label: 'CGST (₹)', key: 'gst_cgst', right: true, mono: true,
                render: r => parseFloat(r.gst_cgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.gst_cgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'SGST (₹)', key: 'gst_sgst', right: true, mono: true,
                render: r => parseFloat(r.gst_sgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.gst_sgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'IGST (₹)', key: 'gst_igst', right: true, mono: true,
                render: r => parseFloat(r.gst_igst) > 0
                  ? <span className="reg-mono-accent">₹{fmt(r.gst_igst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'Total (₹)', key: 'invoice_amount', right: true, mono: true,
                render: r => (
                  <div className="reg-total-cell">
                    <span className="reg-mono-red">₹{fmt(r.invoice_amount)}</span>
                    {getAmountFlag(r.invoice_amount) && (
                      <div className="reg-flag-badge">
                        <Badge
                          text={getAmountFlag(r.invoice_amount).label}
                          variant={getAmountFlag(r.invoice_amount).variant}
                        />
                      </div>
                    )}
                  </div>
                )},
            ]}
            rows={rows.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No purchase invoices found"
          />
        )}
      </Card>
    </PageWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   GST Invoices
═══════════════════════════════════════════════════════════════════════════ */
export function GSTInvoices({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.gstInvoices(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let r = data || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.party?.toLowerCase().includes(q) ||
        x.invoice_number?.toLowerCase().includes(q) ||
        x.voucher_number?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, search]);

  const totalTaxable = (data || []).reduce((s, r) => s + parseFloat(r.taxable || 0), 0);
  const totalGST     = (data || []).reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const totalCGST    = (data || []).reduce((s, r) => s + parseFloat(r.cgst || r.gst_cgst || 0), 0);
  const totalSGST    = (data || []).reduce((s, r) => s + parseFloat(r.sgst || r.gst_sgst || 0), 0);
  const totalIGST    = (data || []).reduce((s, r) => s + parseFloat(r.igst || r.gst_igst || 0), 0);

  return (
    <PageWrap>
      <PageHeader
        title="GST Invoices"
        sub="Sales invoices with GST liability breakdown"
      />

      <StatGrid cols={5}>
        <StatCard label="Invoices"       value={(data || []).length} />
        <StatCard label="Taxable Value"  value={`₹${fmt(totalTaxable)}`} />
        <StatCard label="CGST"           value={`₹${fmt(totalCGST)}`}  accent="amber" />
        <StatCard label="SGST"           value={`₹${fmt(totalSGST)}`}  accent="amber" />
        <StatCard label="IGST"           value={`₹${fmt(totalIGST)}`}  accent="blue"  />
      </StatGrid>

      <Card>
        <CardHeader
          title={`${rows.length} GST Invoice${rows.length !== 1 ? 's' : ''}`}
          right={
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search party or invoice..."
            />
          }
        />
        {loading ? <Skeleton rows={6} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="reg-idx">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => <span className="reg-date">{fmtDate(r.date)}</span> },
              { label: 'Invoice No.', key: 'invoice_number',
                render: r => <Tag>{r.invoiceNumber ?? r.invoice_number ?? r.voucher_number}</Tag> },
              { label: 'Party', key: 'party',
                render: r => (
                  <span className="reg-party-name">
                    {r.party ?? r.raw_party ?? '—'}
                  </span>
                )},
              { label: 'Taxable (₹)', key: 'taxable', right: true, mono: true,
                render: r => <span className="reg-mono">₹{fmt(r.taxable)}</span> },
              { label: 'CGST (₹)', key: 'cgst', right: true, mono: true,
                render: r => parseFloat(r.cgst ?? r.gst_cgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.cgst ?? r.gst_cgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'SGST (₹)', key: 'sgst', right: true, mono: true,
                render: r => parseFloat(r.sgst ?? r.gst_sgst) > 0
                  ? <span className="reg-mono">₹{fmt(r.sgst ?? r.gst_sgst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'IGST (₹)', key: 'igst', right: true, mono: true,
                render: r => parseFloat(r.igst ?? r.gst_igst) > 0
                  ? <span className="reg-mono-accent">₹{fmt(r.igst ?? r.gst_igst)}</span>
                  : <span className="reg-dim">—</span> },
              { label: 'Total GST (₹)', key: 'gst_total', right: true, mono: true,
                render: r => (
                  <strong className="reg-mono-amber">
                    ₹{fmt(r.total ?? r.gst_total)}
                  </strong>
                )},
            ]}
            rows={rows.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No GST invoices found"
          />
        )}
      </Card>
    </PageWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Bank Book
═══════════════════════════════════════════════════════════════════════════ */
export function BankBook({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.bankBook(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch]             = useState('');
  const [selectedAccount, setSelectedAccount] = useState('all');

  const accounts = data?.accounts || [];

  const rows = useMemo(() => {
    let r = data?.entries || [];
    if (selectedAccount !== 'all') {
      r = r.filter(x => x.ledger === selectedAccount);
    }
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.ledger?.toLowerCase().includes(q)         ||
        x.party?.toLowerCase().includes(q)          ||
        x.voucher_number?.toLowerCase().includes(q) ||
        x.narration?.toLowerCase().includes(q)      ||
        x.voucher_type?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, search, selectedAccount]);

  const totalEntries = data?.totalEntries  ?? 0;
  const totalDebits  = data?.totalDebits   ?? 0;
  const totalCredits = data?.totalCredits  ?? 0;
  const netMovement  = data?.netMovement   ?? 0;

  const filteredCredits = rows.reduce((s, r) => parseFloat(r.amount) > 0 ? s + parseFloat(r.amount) : s, 0);
  const filteredDebits  = rows.reduce((s, r) => parseFloat(r.amount) < 0 ? s + Math.abs(parseFloat(r.amount)) : s, 0);

  const getVoucherVariant = (type) => {
    if (!type) return 'gray';
    const t = type.toLowerCase();
    if (t.includes('receipt'))  return 'green';
    if (t.includes('payment'))  return 'red';
    if (t.includes('contra'))   return 'blue';
    if (t.includes('journal'))  return 'amber';
    return 'gray';
  };

  return (
    <PageWrap>
      <PageHeader
        title="Bank Book"
        sub="All bank account transactions for the period"
      />

      <StatGrid cols={4}>
        <StatCard label="Total Entries"  value={totalEntries} />
        <StatCard label="Total Credits"  value={`₹${fmt(totalCredits)}`} accent="green" />
        <StatCard label="Total Debits"   value={`₹${fmt(totalDebits)}`}  accent="red"   />
        <StatCard
          label="Net Movement"
          value={`${netMovement >= 0 ? '+' : '-'}₹${fmt(Math.abs(netMovement))}`}
          accent={netMovement >= 0 ? 'green' : 'red'}
        />
      </StatGrid>

      {/* ── Per-account filter cards ── */}
      {accounts.length > 0 && (
        <div className="reg-account-cards">
          {/* All accounts card */}
          <div
            onClick={() => setSelectedAccount('all')}
            style={{
              background: selectedAccount === 'all' ? 'var(--accent)' : 'var(--bg2)',
              border: `1px solid ${selectedAccount === 'all' ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: '10px 16px',
              cursor: 'pointer',
              minWidth: 140,
              flex: '1 1 140px',
            }}
          >
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: selectedAccount === 'all' ? '#fff' : 'var(--text3)',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
            }}>
              All Accounts
            </div>
            <div style={{
              fontSize: 18, fontWeight: 600,
              color: selectedAccount === 'all' ? '#fff' : 'var(--text)',
            }}>
              {totalEntries} entries
            </div>
          </div>

          {accounts.map(acc => (
            <div
              key={acc.account}
              onClick={() => setSelectedAccount(
                selectedAccount === acc.account ? 'all' : acc.account
              )}
              style={{
                background: selectedAccount === acc.account ? 'var(--accent)' : 'var(--bg2)',
                border: `1px solid ${selectedAccount === acc.account ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-lg)',
                padding: '10px 16px',
                cursor: 'pointer',
                minWidth: 140,
                flex: '1 1 140px',
              }}
            >
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: selectedAccount === acc.account ? '#fff' : 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                marginBottom: 4,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {acc.account}
              </div>
              <div style={{
                fontSize: 16, fontWeight: 600,
                color: selectedAccount === acc.account ? '#fff' : 'var(--text)',
                marginBottom: 4,
              }}>
                {acc.transaction_count} entries
              </div>
              <div style={{
                fontSize: 11,
                color: selectedAccount === acc.account ? 'rgba(255,255,255,0.8)' : 'var(--text3)',
              }}>
                ↑ ₹{fmt(acc.total_credits)} · ↓ ₹{fmt(acc.total_debits)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Table ── */}
      <Card>
        <CardHeader
          title={
            selectedAccount === 'all'
              ? `${rows.length} Entr${rows.length !== 1 ? 'ies' : 'y'}`
              : `${rows.length} Entr${rows.length !== 1 ? 'ies' : 'y'} — ${selectedAccount}`
          }
          right={
            <div className="reg-header-right">
              {selectedAccount !== 'all' && (
                <div className="reg-header-amounts">
                  <span className="reg-credit">↑ ₹{fmt(filteredCredits)}</span>
                  &nbsp;·&nbsp;
                  <span className="reg-debit">↓ ₹{fmt(filteredDebits)}</span>
                </div>
              )}
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search ledger, party, type..."
              />
            </div>
          }
        />
        {loading ? <Skeleton rows={6} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="reg-idx">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => <span className="reg-date">{fmtDate(r.date)}</span> },
              { label: 'Voucher', key: 'voucher_number',
                render: r => <Tag>{r.voucher_number ?? r.voucherNumber}</Tag> },
              { label: 'Type', key: 'voucher_type',
                render: r => (
                  <Badge
                    text={r.voucher_type ?? r.voucherType ?? '—'}
                    variant={getVoucherVariant(r.voucher_type ?? r.voucherType)}
                  />
                )},
              { label: 'Bank Account', key: 'ledger',
                render: r => <span className="reg-account-name">{r.ledger ?? '—'}</span> },
              { label: 'Party', key: 'party',
                render: r => <span className="reg-party">{r.party ?? '—'}</span> },
              { label: 'Narration', key: 'narration', maxWidth: 180,
                render: r => <span className="reg-narration">{r.narration || '—'}</span> },
              { label: 'Amount (₹)', key: 'amount', right: true, mono: true,
                render: r => {
                  const a = parseFloat(r.amount ?? 0);
                  return (
                    <span style={{
                      color: a >= 0 ? 'var(--green)' : 'var(--red)',
                      fontWeight: 600,
                      fontFamily: 'var(--mono)',
                      fontSize: 12,
                    }}>
                      {a >= 0 ? '+' : '-'}₹{fmt(Math.abs(a))}
                    </span>
                  );
                }},
            ]}
            rows={rows.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No bank entries found"
          />
        )}
      </Card>
    </PageWrap>
  );
}
